<!-- @format -->
<template>
    <div :class="[{ 'dropdown-shown': step === 'contactExists' }, 'contact-add-widget']">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title || 'Contacts' }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="step === 'loading'" class="loading-step" />
                <div v-else-if="step === 'limitReached'" class="limit-reached-step">
                    <img
                        src="../../assets/images/SVG/error-confirmation.svg"
                        class="error-confirmation"
                    />
                    <div class="description">
                        The maximum amount of contacts under your current subscription has been
                        reached. Please upgrade your subscription to add more contacts.
                    </div>
                    <app-button class="upgrade-button" @click="goToSubscriptionPlans">
                        Upgrade
                    </app-button>
                </div>
                <div v-else-if="step === 'email'" class="add-contact-step scrub-step">
                    <div class="contact-form-row">
                        <app-input
                            :data-locator="getDataLocator('add-email-input')"
                            v-model="contact.email"
                            :label="'Enter an Email'"
                            :placeholder="'email@email.com'"
                        />
                    </div>
                </div>
                <div
                    v-else-if="step === 'addContact' || step === 'verifyContact'"
                    class="add-contact-step"
                >
                    <div class="contact-form-row contact-profile-image">
                        <image-upload
                            id="contact-image-upload"
                            :uploadUrl="uploadUrl"
                            :placeholderImage="placeholderImage"
                            :providedImageUrl="contact.profileImageUrl"
                            orientation="horizontal"
                            @fileUploadSuccess="updateContactImageUrl"
                        />
                    </div>
                    <div class="contact-form-row">
                        <div class="input-container">
                            <label>Company</label>
                            <company-search
                                :data-locator="getDataLocator('search-company-input')"
                                v-model="company"
                                :allowManage="true"
                                :clientId="contact.clientId"
                            />
                        </div>
                        <app-input
                            :data-locator="getDataLocator('first-name-input')"
                            v-model="contact.firstName"
                            :label="'First Name'"
                            :placeholder="'Sterling'"
                        />
                        <app-input
                            :data-locator="getDataLocator('last-name-input')"
                            v-model="contact.lastName"
                            :label="'Last Name'"
                            :placeholder="'Archer'"
                        />
                        <app-input
                            :data-locator="getDataLocator('title-input')"
                            v-model="contact.title"
                            :label="'Title'"
                            :placeholder="'Job Title...'"
                        />
                        <app-input
                            :data-locator="getDataLocator('linkedin-input')"
                            v-model="contact.linkedinUrl"
                            :label="'LinkedIn URL'"
                            :placeholder="'www...'"
                        />
                        <app-input
                            :data-locator="getDataLocator('phone-input')"
                            v-model="contact.phone"
                            :label="'Phone'"
                            :placeholder="'(555) 867-5309'"
                        />
                        <app-input
                            :data-locator="getDataLocator('city-input')"
                            v-model="contact.city"
                            :label="'City'"
                            :placeholder="'What city'"
                        />
                        <app-input
                            :data-locator="getDataLocator('state-input')"
                            v-model="contact.region"
                            :label="'State'"
                            :placeholder="'State'"
                        />
                        <label>Salesperson</label>
                        <salesperson-dropdown
                            :data-locator="getDataLocator('salesperson-search-input')"
                            v-model="contact.selectedSalesperson"
                            :initialId="userId"
                            :clientId="clientId"
                        />
                    </div>
                </div>
                <div
                    v-else-if="step === 'scrubbing' || step === 'saving'"
                    class="add-contact-step scrubbing-step"
                >
                    <quick-add-contact-svg />
                </div>
                <div v-else-if="step === 'addCompany'">
                    <div class="company-form-row company-profile-image">
                        <image-upload
                            id="company-image-upload"
                            :uploadUrl="uploadUrl"
                            :placeholderImage="companyPlaceholderImage"
                            :providedImageUrl="company.logoUrl"
                            orientation="horizontal"
                            @fileUploadSuccess="updateCompanyImageUrl"
                        />
                    </div>
                    <div class="company-form-row">
                        <div class="input-container">
                            <app-input v-model="company.name" :label="'Company Name'" />
                        </div>
                        <div class="input-container">
                            <app-input v-model="company.phone" :label="'Phone'" />
                        </div>
                    </div>
                    <div class="company-form-row">
                        <div class="input-container">
                            <app-input v-model="company.address" :label="'Address'" />
                        </div>
                        <div class="input-container">
                            <app-input v-model="company.email" :label="'Email'" />
                        </div>
                    </div>
                    <div class="company-form-row">
                        <div class="input-container">
                            <app-input v-model="company.address2" :label="'Address 2'" />
                        </div>
                        <div class="input-container">
                            <app-input v-model="company.website" :label="'Website'" />
                        </div>
                    </div>
                    <div class="company-form-row">
                        <div class="input-container">
                            <app-input v-model="company.city" :label="'Zip Code'" />
                        </div>
                        <div class="input-container">
                            <app-input v-model="company.region" :label="'Region'" />
                        </div>
                    </div>
                </div>
                <div v-else-if="step === 'selectList'">
                    <div class="input-container">
                        <label>Lists</label>
                        <list-search :clientId="contact.clientId" @input="addList($event)" :data-locator="getDataLocator('list-search-input')"/>
                        <div class="lists">
                            <div
                                v-for="list in contact.lists"
                                :key="list.id"
                                class="list"
                                :class="list.class"
                            >
                                {{ list.name }}
                                <x-svg @click.native="removeList(list)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <transition name="slide-down">
                <div v-if="step === 'contactExists'" class="add-contact-step contact-exists-step">
                    <div class="contact-exists-container">
                        <div class="contact">
                            <h1>Contact Already Exists</h1>
                            <div
                                class="contact-image"
                                :style="{
                                    'background-image': `url('${
                                        contact.profileImageUrl ||
                                        require('@/assets/images/avatar.png')
                                    }')`,
                                }"
                            ></div>
                            <h3>
                                {{
                                    `${
                                        (contact.firstName ? contact.firstName + ' ' : '') +
                                        (contact.lastName ? contact.lastName : '')
                                    }`
                                }}
                            </h3>
                            <div class="button-container">
                                <app-button class="white-btn" @click="goToContact">
                                    Go To Contact
                                </app-button>
                                <app-button class="transparent-btn" @click="goToEmailStep">
                                    Try Again
                                </app-button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="step === 'email'"
                        class="transparent-btn"
                        @click="goToAddContactStep"
                    >
                        No Email?
                    </app-button>
                    <app-button
                        v-show="showBackButton"
                        class="transparent-btn"
                        @click="goToPreviousStep"
                    >
                        Back
                    </app-button>
                    <app-button
                        v-if="step !== 'limitReached' && step !== 'loading'"
                        :loading="steps[step].loading"
                        @click="steps[step].action"
                    >
                        {{ steps[step].buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const CompanySearch = () => import('@/components/company/company-search');
const ImageUpload = () => import('@/components/common/image-upload');
const ListSearch = () => import('@/components/list/list-search');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const QuickAddScrubbingSvg = () => import('@/components/svg/quick-add/quick-add-scrubbing-svg');
const SalespersonDropdown = () => import('@/components/user/salesperson-dropdown');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const XSvg = () => import('@/components/svg/connectwise/x-svg');

import EventBus from '@/scripts/event-bus.js';
import { QaMixin } from '@/mixins/qa-mixin';

export default {
    name: 'contact-add-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'company-search': CompanySearch,
        'image-upload': ImageUpload,
        'list-search': ListSearch,
        'loading-screen-svg': LoadingScreenSvg,
        'quick-add-contact-svg': QuickAddScrubbingSvg,
        'salesperson-dropdown': SalespersonDropdown,
        'x-svg': XSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [QaMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,

            contact: {
                id: null,
                email: null,
                firstName: null,
                lastName: null,
                title: null,
                linkedinUrl: null,
                phone: null,
                city: null,
                profileImageUrl: null,
                company: null,
                bio: null,
                clientId: null,
                selectedSalesperson: null,
                listId: null,
                lists: [],
            },

            company: {
                id: null,
                name: '',
                logoUrl: '',
                phone: null,
                address: '',
                address2: '',
                city: '',
                region: '',
                country: '',
                countryCode: null,
                domain: null,
                linkedinUrl: '',
                facebookUrl: '',
                twitterUrl: '',
                youtubeUrl: '',
                description: '',
                approximateEmployeeCount: null,
                email: '',
                website: '',
                zipCode: null,
            },

            step: null,

            steps: {
                loading: {
                    title: 'Loading...',
                    buttonText: '',
                    action: () => {},
                    loading: true,
                },

                limitReached: {
                    title: 'Contact Limit Reached',
                    buttonText: '',
                    action: () => {},
                    loading: false,
                },

                email: {
                    title: 'Add Contact',
                    buttonText: 'Next',
                    action: this.scrubEmail,
                    loading: false,
                },

                scrubbing: {
                    title: 'Scrubbing',
                    buttonText: 'Scrubbing',
                    action: () => {},
                    loading: true,
                },

                contactExists: {
                    title: '',
                    buttonText: '',
                    action: () => {},
                    loading: false,
                },

                addContact: {
                    title: 'Add Contact',
                    buttonText: 'Next',
                    action: this.goToSelectListStep,
                    loading: false,
                },

                verifyContact: {
                    title: 'Add Contact',
                    buttonText: 'Next',
                    action: this.goToSelectListStep,
                    loading: false,
                },

                selectList: {
                    title: 'Select List',
                    buttonText: 'Submit',
                    action: this.submitContact,
                    loading: false,
                },

                saving: {
                    title: 'Saving...',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },

            currentPlan: {
                contactLimit: 0,
                contactCount: 0,
            },

            initialCompanyId: this.widget.options.companyId,

            uploadUrl: '/api/Files',
            placeholderImage: require('@/assets/images/avatar.png'),
            companyPlaceholderImage: require('@/assets/images/company-avatar.png'),
        };
    },

    computed: {
        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        userId() {
            return this.$store.state.user.id;
        },

        difference() {
            return this.currentPlan.contactLimit
                ? this.currentPlan.contactLimit - this.currentPlan.contactCount
                : null;
        },

        isOverLimit() {
            return this.difference != null ? this.difference <= 0 : null;
        },

        showBackButton() {
            return (
                this.step === 'addContact' ||
                this.step === 'verifyContact' ||
                this.step === 'selectList'
            );
        },
    },

    created() {
        this.contact.clientId = this.widget.options.clientId;
        this.checkSubscription();

        if (this.initialCompanyId) {
            this.getInitialCompany();
        }
    },

    methods: {
        goToPreviousStep() {
            switch (this.step) {
                case 'addContact':
                    this.goToStep('email');
                    break;
                case 'verifyContact':
                    this.goToStep('addContact');
                    break;
                case 'selectList':
                    this.goToStep('verifyContact');
                    break;
            }
        },

        goToStep(step) {
            this.step = step;
        },

        getNextStep() {
            if (this.isOverLimit) {
                this.step = 'limitReached';
            } else {
                this.step = 'email';
            }
        },

        getInitialCompany() {
            const select = '$select=Id,Name,LogoUrl';
            const filter = `$filter=Id eq ${this.initialCompanyId}`;
            const restUrl = `/service/Companies?${filter}&${select}`;

            this.$api
                .get(restUrl)
                .then((response) => {
                    this.company = {
                        id: response.data.value[0].Id,
                        name: response.data.value[0].Name,
                        imageUrl: response.data.value[0].LogoUrl,
                    };
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get initial company', { error });
                });
        },

        checkSubscription() {
            this.step = 'loading';

            this.getContactLimit().then(this.getContactCount).finally(this.getNextStep);
        },

        goToSubscriptionPlans() {
            this.$router.push(`${this.routeBaseUrl}/Account/Billing/`);
            this.closeWidget();
        },

        getContactLimit() {
            return new Promise((resolve, reject) => {
                var filterParam = `$filter=Id eq ${this.contact.clientId}`;
                var selectParam = `$select=Promo`;
                var expandParam = `$expand=Promo($select=ContactLimit)`;
                var restUrl = `/service/Clients?${filterParam}&${selectParam}&${expandParam}`;

                this.$api
                    .get(restUrl)
                    .then((response) => {
                        this.currentPlan.contactLimit = response.data.value[0].Promo.ContactLimit;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(`Failed to get contact limit`, { error });
                        reject();
                    });
            });
        },

        getContactCount() {
            return new Promise((resolve, reject) => {
                var filterParam = `$filter=ClientId eq ${this.contact.clientId}`;
                var restUrl = `/service/Contacts?${filterParam}&$top=0&$count=true`;

                this.$api
                    .get(restUrl)
                    .then((response) => {
                        this.currentPlan.contactCount = response.data['@odata.count'];
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(`Failed to get contact count`, { error });
                        reject();
                    });
            });
        },

        scrubEmail() {
            this.step = 'scrubbing';
            var query = 'email=' + this.contact.email + '&clientId=' + this.contact.clientId;

            this.$api
                .get(`/api/Contacts/Search?${query}`)
                .then((response) => {
                    this.contact.firstName = response.data[0].FirstName;
                    this.contact.lastName = response.data[0].LastName;
                    this.contact.title = response.data[0].Title;
                    this.contact.linkedinUrl = response.data[0].LinkedinUrl;
                    this.contact.phone = response.data[0].Phone;
                    this.contact.city = response.data[0].City;
                    this.contact.profileImageUrl = response.data[0].ProfileImageUrl;
                    this.contact.bio = response.data[0].FullContactBio;

                    if (response.data[0].Id) {
                        this.contact.id = response.data[0].Id;
                        this.goToContactExistsStep();
                    } else {
                        this.checkForCompany();
                        this.goToVerifyContactStep();
                    }
                })
                .catch((error) => {
                    this.checkForCompany();
                    this.goToVerifyContact();
                });
        },

        goToEmailStep() {
            this.clearContact();
            this.step = 'email';
        },

        goToContactExistsStep() {
            this.step = 'contactExists';
        },

        goToAddContactStep() {
            this.step = 'addContact';
        },

        goToVerifyContactStep() {
            this.step = 'verifyContact';
        },

        goToAddCompanyStep() {
            this.step = 'addCompany';
        },

        goToSelectListStep() {
            this.step = 'selectList';
        },

        clearContact() {
            this.contact = {
                id: null,
                clientId: this.contact.clientId,
                selectedSalesperson: this.contact.selectedSalesperson,
                email: null,
                firstName: null,
                lastName: null,
                title: null,
                linkedinUrl: null,
                phone: null,
                city: null,
                region: null,
                profileImageUrl: null,
                company: null,
                bio: null,
                listId: null,
                lists: [],
            };
        },

        updateContactImageUrl(imageUrl) {
            this.contact.profileImageUrl = imageUrl;
        },

        updateCompanyImageUrl(imageUrl) {
            this.company.logoUrl = imageUrl;
        },

        goToContact() {
            this.$router.push(`/Contacts/${this.contact.id}`);
            this.closeWidget();
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        checkForCompany() {
            if (this.contact.email) {
                this.company.domain = this.contact.email.substring(
                    this.contact.email.lastIndexOf('@') + 1
                );

                this.$api
                    .get(
                        `/api/Clients/${this.contact.clientId}/Companies/Search?website=${this.company.domain}`
                    )
                    .then((response) => {
                        if (response.data[0]) {
                            this.company.id = response.data[0].Id;
                            this.company.name = response.data[0].Name;
                            this.company.imageUrl = response.data[0].LogoUrl;
                        }
                    })
                    .catch((error) => {
                        this.company.id = null;
                    });
            }
        },

        addList(newList) {
            var exists = this.contact.lists.some((list) => {
                return list.id === newList.id;
            });

            if (!exists) {
                newList.class = 'solid-grey-bg';
                this.contact.lists.push(newList);
            }
        },

        saveContact() {
            return new Promise((resolve, reject) => {
                var contact = {
                    ClientId: this.contact.clientId,
                    Email: this.contact.email,
                    FirstName: this.contact.firstName,
                    LastName: this.contact.lastName,
                    Title: this.contact.title,
                    LinkedinUrl: this.contact.linkedinUrl,
                    Phone: this.contact.phone,
                    City: this.contact.city,
                    Region: this.contact.region,
                    ProfileImageUrl: this.contact.profileImageUrl,
                    CompanyId: this.company.id ? this.company.id : null,
                    SalesPersonId: this.contact.selectedSalesperson
                        ? this.contact.selectedSalesperson.id
                        : null,
                };

                this.$api
                    .post(`/api/Contacts`, contact)
                    .then((response) => {
                        var contact = response.data;
                        var name = (contact.FirstName || '') + ' ' + (contact.LastName || '');
                        this.contact.id = contact.Id;

                        resolve();
                    })
                    .catch(reject);
            });
        },

        insertNoteActivity() {
            return new Promise((resolve, reject) => {
                if (this.contact.bio) {
                    var noteActivity = {
                        activityDate: this.$moment.utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                        contactId: this.contact.id,
                        isTrash: false,
                        salesNote: this.contact.bio,
                    };

                    this.$api.post('/api/NoteActivities', noteActivity).then(resolve).catch(reject);
                } else {
                    resolve();
                }
            });
        },

        saveList() {
            return new Promise((resolve, reject) => {
                var request = this.contact.lists.map((list) => list.id);

                this.$api
                    .put(`/api/Contacts/${this.contact.id}/Lists`, request)
                    .then(resolve)
                    .catch(reject);
            });
        },

        submitContact() {
            this.step = 'saving';

            this.saveContact()
                .then(this.insertNoteActivity)
                .then(this.saveList)
                .then(() => {
                    var notification = {
                        iconUrl:
                            this.contact.profileImageUrl || require('@/assets/images/avatar.png'),
                        subIcon: 'success',
                        message: `${this.$helpers.getName(
                            this.contact.firstName,
                            this.contact.lastName
                        )} was added.`,
                        actionLabel: 'View',
                        action: () => {
                            this.goToContact();
                            EventBus.$emit('close-notification', this.notification);
                        },
                    };

                    EventBus.$emit('close-widget', this.widget);
                    EventBus.$emit('show-notification', notification);
                    EventBus.$emit('contact:update');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your contact.',
                        { error },
                    );
                });
        },

        removeList(list) {
            var listIndex = this.contact.lists.indexOf(list);
            this.contact.lists.splice(listIndex, 1);
        },
    },
};
</script>

<style>
.contact-add-widget .add-contact-step {
    width: 100%;
}

.contact-add-widget .message-wrapper {
    text-align: center;
    margin-bottom: 25px;
}

.contact-add-widget .add-contact-step .contact-form-row {
    display: flex;
    flex-direction: column;
}

.contact-add-widget .add-contact-step .contact-form-row.contact-profile-image {
    flex-direction: row;
    align-items: center;
    margin: 0 auto 25px auto;
}

.contact-add-widget .add-contact-step .contact-form-row.contact-profile-image .file-input {
    display: none;
}

.contact-add-widget .add-contact-step .contact-form-row.contact-profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.contact-add-widget .input-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 24px;
}

.contact-add-widget .add-contact-step.scrub-step .contact-form-row {
    flex-direction: column;
}

.contact-add-widget .add-contact-step.contact-exists-step,
.contact-add-widget .add-contact-step.company-exists-step {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    height: 100%;
    width: 100%;
    background: rgb(122, 91, 255);
    background: linear-gradient(0deg, rgba(122, 91, 255, 1) 0%, rgba(227, 141, 238, 1) 100%);
}

.contact-add-widget .add-contact-step.contact-exists-step.slide-down-enter-active,
.add-contact-step.contact-exists-step.slide-down-leave-active,
.contact-add-widget .add-contact-step.company-exists-step.slide-down-enter-active,
.add-contact-step.company-exists-step.slide-down-leave-active {
    transition: all 0.3s ease 0s;
}

.contact-add-widget .add-contact-step.contact-exists-step.slide-down-enter,
.add-contact-step.contact-exists-step.slide-down-leave-to,
.contact-add-widget .add-contact-step.company-exists-step.slide-down-enter,
.add-contact-step.company-exists-step.slide-down-leave-to {
    top: -100%;
}

.contact-add-widget .contact-exists-step .contact-exists-container,
.contact-add-widget .company-exists-step .company-exists-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
}

.contact-add-widget .contact-exists-step .contact-exists-container .contact,
.contact-add-widget .company-exists-step .company-exists-container .company {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.contact-add-widget .contact-exists-step .contact-exists-container .contact h1,
.contact-add-widget .company-exists-step .company-exists-container .company h1 {
    color: #ffffff;
    margin-bottom: 53px;
}

.contact-add-widget .contact-exists-container .contact-image,
.contact-add-widget .company-exists-container .company-image {
    height: 131px;
    width: 131px;
    border-radius: 50%;
    background-size: cover;
    background-position: 50%;
    margin-bottom: 10px;
}

.contact-add-widget .contact-exists-step .contact h3,
.contact-add-widget .company-exists-step .company h3 {
    color: #ffffff;
    margin-bottom: 32px;
    font-family: 'Roboto', sans-serif;
    font-weight: 600;
}

.contact-add-widget .contact-exists-step .contact .button-container,
.contact-add-widget .company-exists-step .company .button-container {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.contact-add-widget .contact-exists-step .contact .button-container .btn,
.contact-add-widget .company-exists-step .company .button-container .btn {
    margin-bottom: 15px;
}

.contact-add-widget .contact-exists-step .contact .button-container .btn.white-btn,
.contact-add-widget .company-exists-step .company .button-container .btn.white-btn {
    background: #ffffff;
    color: #8360fe;
    border: none;
}

.contact-add-widget .contact-exists-step .contact .button-container .btn.white-btn:hover,
.contact-add-widget .company-exists-step .company .button-container .btn.white-btn:hover {
    background: #382153;
    color: #ffffff;
    border: none;
}

.contact-add-widget .contact-exists-container .contact .button-container .btn.transparent-btn,
.contact-add-widget .company-exists-container .company .button-container .btn.transparent-btn {
    background: transparent;
    color: #ffffff;
    width: 100%;
}

.contact-add-widget
    .contact-exists-step
    .contact-exists-container
    .contact
    .button-container
    .btn.transparent-btn:hover,
.contact-add-widget
    .company-exists-step
    .company-exists-container
    .company
    .button-container
    .btn.transparent-btn:hover {
    background: #18aeff;
}

.contact-add-widget .add-contact-step.scrubbing-step svg {
    width: 100%;
}

.contact-add-widget .action-container {
    display: flex;
}

.contact-add-widget .action-container .btn {
    margin-left: 15px;
}

.contact-add-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.contact-add-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.contact-add-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.contact-add-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.contact-add-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.contact-add-widget .company-form-row {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-pack: justify;
    -ms-flex-pack: justify;
    justify-content: space-between;
}

.contact-add-widget .company-form-row .input-container {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    width: 45%;
    margin-bottom: 24px;
}

.contact-add-widget .company-profile-image {
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    max-width: 250px;
    margin: 0 auto 25px auto;
}
.contact-add-widget .lists {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.contact-add-widget .lists .list {
    color: #ffffff;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}
.contact-add-widget .lists .list svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.contact-add-widget .lists .list svg path {
    fill: #ffffff;
}

.contact-add-widget .list-search {
    margin-bottom: 10px;
}

.contact-add-widget .company-form-row .input-container {
    width: 75%;
}

.contact-add-widget .company-form-row .input-container label.placeholder {
    top: 35px;
}

.contact-add-widget .company-form-row .input-container input {
    height: 42px;
}

.contact-add-widget .content-wrapper .limit-reached-step {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.contact-add-widget .content-wrapper .limit-reached-step .error-confirmation {
    width: 100px;
    height: auto;
    margin: 0 auto;
}

.contact-add-widget .content-wrapper .limit-reached-step .description {
    margin: 10px 0;
}

.contact-add-widget .content-wrapper .limit-reached-step .upgrade-button {
    width: 125px;
    margin: 15px auto;
}
</style>
